import i18n from '../i18n';
import { roundToDynamicFloating } from '../components/Chart/analyseUtils';
import { normalizeArabicToPersian } from './string';

export const roundDecimal =
  (afterRadix: number = 2) =>
  (decimalNumber: number): number => {
    const factor = Math.pow(10, afterRadix);
    return Math.round(decimalNumber * factor) / factor;
  };

export const persianDigits = ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '۰'];
const englishDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

// convert Persian digits => English digits in a string
export const englishNumber = (digit: string) => {
  for (let i = 0; i < englishDigits.length; i++) {
    digit = digit.replace(new RegExp(persianDigits[i], 'g'), englishDigits[i]);
  }
  return digit;
};

export const preprocessForSearch = (text: string) => {
  return englishNumber(normalizeArabicToPersian(text).toLowerCase());
};

export const convertDigitToProperFormat = (digit: string | number) => {
  if (i18n.language === 'fa') {
    const isNumber = isANumber(Number(digit));
    if (typeof digit === 'number') {
      digit = digit.toString();
    }
    if (!digit) digit = '';
    for (let i = 0; i < persianDigits.length; i++) {
      digit = (digit as string).replace(
        new RegExp(englishDigits[i], 'g'),
        persianDigits[i]
      );
    }
    if (isNumber) digit = digit.replace('.', '٫');
    return digit as string;
  } else {
    return digit as string;
  }
};

export const convertNumbersToProperFormat = (
  digit: number,
  fractionCount: number | null = null
) => {
  if (digit === null) return null as any;
  if (!isANumber(digit)) return digit;
  if (isANumber(fractionCount)) {
    const ret = new Intl.NumberFormat(i18n.language, {
      useGrouping: true,
    }).format(+digit.toFixed(fractionCount!) as number);
    return ret;
  }
  return new Intl.NumberFormat(i18n.language, { useGrouping: true }).format(
    roundToDynamicFloating(digit)
  );
};

export const bytesToSize = (bytes: number) => {
  const sizes = [
    i18n.t('Byte', { ns: 'taskManager' }),
    i18n.t('KB', { ns: 'taskManager' }),
    i18n.t('MB', { ns: 'taskManager' }),
    i18n.t('GB', { ns: 'taskManager' }),
    i18n.t('TB', { ns: 'taskManager' }),
  ];
  if (bytes === 0) {
    return 'n/a';
  }
  const res = Math.floor(Math.log(bytes) / Math.log(1024)).toString();
  const i = parseInt(res, 10);
  if (i === 0) {
    return `${convertDigitToProperFormat(bytes)} ${sizes[i]}`;
  }
  return `${convertDigitToProperFormat((bytes / 1024 ** i).toFixed(1))} ${sizes[i]}`;
};

export const getRandomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const randomNumber = (length: number): number => {
  if (length <= 0) {
    return 0;
  }
  return Math.ceil(Math.random() * Math.pow(10, length));
};

export const isANumber = (value: any) => {
  return typeof value === 'number' && !isNaN(value);
};

export const getInternationalPhone = (input: string) => {
  const inputValue = englishNumber(input);
  const inputLength = inputValue.length;
  const slicedNumber = inputValue.slice(inputLength - 10, inputLength);
  const phoneNumber = `0098${slicedNumber}`;

  return { inputValue, phoneNumber };
};

export const numberToTime = (input: string | number) => {
  const val = Number(input);
  const isValid = isANumber(val);

  if (!isValid && input.toString() !== '0') return String(input);

  const minutes = val % 60;
  const hour = Math.floor(val / 60);

  let res = '';

  if (hour) {
    res += `${i18n.t('hour', { ns: 'taskManager', hour })}`;
  }
  if (minutes || (minutes.toString() === '0' && !hour)) {
    res += hour ? ` ${i18n.t('and', { ns: 'taskManager' })} ` : '';
    res += `${i18n.t('minutes', { ns: 'taskManager', minutes: minutes })} `;
  }

  return convertDigitToProperFormat(res);
};
